import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderRow,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    Search,
    SortDirection,
    TextButton,
    Typography,
} from "@episerver/ui-framework";

import { Organization, OrganizationSearchResult, SearchParams, ServerSortType } from "../../OrganizationManager/model/organization";
import { Contact } from "../model/contact";
import { debounce } from "lodash-es";
import axios from "axios";
import { Snackbar, SnackbarAction } from "@rmwc/snackbar";

interface AddParentOrganizationProps {
    contactId: string | null;
    setParentId: ((parentId: string | null) => void) | undefined;
    readOnly: boolean | null;
}

const AddParentOrganization = (props: AddParentOrganizationProps) => {

    const [defaultSearchParams, setDefaultSearchParams] = useState<SearchParams>({
        query: '',
        sortField: 'name',
        sortType: ServerSortType.Asc,
        startIndex: 0,
        recordsToRetrieve: 25,
        excludedOrgId: null
    } as SearchParams);
    const [organizations, setOrganizations] = useState<Organization[]>([] as Organization[]);
    const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
    var { contactId } = props;
    const [totalCount, setTotalCount] = useState<number>(0);
    const [parentName, setParentName] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const mounted = useRef(false);
    const url = window && "https://" +  window.location.host + "/EPiServer/Commerce/";
    const searchOrganizations = async (params: SearchParams): Promise<any> => {
        const res = await axios.get<OrganizationSearchResult>(url + "Organizations/Search", { params });
        return res.data;
    };

    const getParentName = async (): Promise<null | string> => {
        if (!contactId) {
            return '';
        }
        const contactResponse = await axios.get<Contact>(url + `Contacts/Get?contactId=${contactId}`);
        if (!contactResponse.data || !contactResponse.data.ownerId) {
            return '';
        }
        const orgResponse = await axios.get<Organization>(url + `Organizations/Get?organizationId=${contactResponse.data.ownerId}`);
        if (!orgResponse.data) {
            return '';
        }
        return orgResponse.data.name;
    };

    useEffect(() => {
        mounted.current = true;

        searchOrganizations(defaultSearchParams).then((data) => {
            setOrganizations(data.organizations);
            setTotalCount(data.totalRecords);
        });

        getParentName().then((name: string | null) => {setParentName(name)})
        return () => { mounted.current = true; };
    }, [contactId]);


    const columns: Array<{ selector: string; title: string; sortable: boolean }> = [
        { selector: "Name", title: "Name", sortable: true },
        { selector: "PayerId", title: "Payer Id", sortable: true },
        { selector: "MasterPayerId", title: "Master Payer Id", sortable: true },
        { selector: "OrganizationType", title: "Type", sortable: true },
    ];

    const handleSearch = (value: string) => debouncedSearch(value);
    const debouncedSearch = useCallback(debounce((nextValue: string) => onSearch(nextValue), 500), [],);
    const onSearch = (value: string): void => {
        const params = defaultSearchParams;
        params.query = value;
        searchOrganizations(params).then((data) => {
            setDefaultSearchParams(params);
            setOrganizations(data.organizations);
            setTotalCount(data.totalRecords);
        });
    };

    const onSort = (column: string, direction: SortDirection): void => {
        const params = defaultSearchParams;
        params.sortField = column;
        params.sortType = direction === SortDirection.Ascending || SortDirection.Unsorted ? ServerSortType.Asc : ServerSortType.Desc;
        searchOrganizations(params).then((data) => {
            setDefaultSearchParams(params);
            setOrganizations(data.organizations);
            setTotalCount(data.totalRecords);
        });
    };

    const isSelected = (row: Organization): boolean => {
        return row.primaryKeyId === selectedOrganization;
    };

    const onSubmit = async (): Promise<void> => {
        if (!selectedOrganization || selectedOrganization === '') {
            setErrorOpen(true);
            setError('Invalid organization was chosen as parent');
        }

        if (props.setParentId) {
            props.setParentId(selectedOrganization);
        }
       
        setIsOpen(false);
    };

    const renderRow = (rowData: Organization): React.ReactElement => {
        return (
            <DataTableRow
                rowId={rowData.primaryKeyId!}
                key={rowData.primaryKeyId}
                selected={isSelected(rowData)}
                onClick={() => {
                    setSelectedOrganization(rowData.primaryKeyId);
                    setParentName(rowData.name);
                }}
            >
                <DataTableCell>{rowData.name}</DataTableCell>
                <DataTableCell>{rowData.extendedProperties?.find(property => property.name === "PayerId")?.value ?? ""}</DataTableCell>
                <DataTableCell>{rowData.extendedProperties?.find(property => property.name === "MasterPayerId")?.value ?? ""}</DataTableCell>
                <DataTableCell>{rowData.organizationType ?? ""}</DataTableCell>
            </DataTableRow>
        );
    };

    const clearParent = () => {
        setParentName('');
        if (props.setParentId) {
            props.setParentId(null);
        }
    }

    return (
        <Fragment>
            <Typography use="body1" className="p-5">{parentName ?? ''}</Typography>
            {!props.readOnly &&
                <div className="edit-form-buttons right-buttons">
                    <TextButton onClick={() => {
                        setIsOpen(true);
                    }}>
                        {"Select parent organization"}
                    </TextButton>
                    <TextButton onClick={() => {
                        clearParent();
                    }}>
                        {"Clear parent"}
                    </TextButton>
                </div>
            }
            <Dialog
                className="child-organization__add-form"
                open={isOpen}
                title="Select parent organization"
                dismissLabel="Cancel"
                confirmLabel="Add"
                enableConfirm={selectedOrganization !== null}
                onInteraction={(evt) => {
                    if (evt.detail.action === "confirm") {
                        onSubmit();
                    } else {
                        setIsOpen(false);
                    }
                }}
            >
                <div className="organization-list">
                    <DataTable>
                        <DataTableToolbar className="organization-list__toolbar">
                            <Search onValueChange={handleSearch} onSearch={handleSearch} value={defaultSearchParams.query} />
                        </DataTableToolbar>
                        <DataTableContent>
                            <DataTableHeaderRow>
                                <React.Fragment>
                                    {columns.map((column) => {
                                        if (column.sortable)
                                            return (
                                                <DataTableColumnHeaderCell
                                                    key={column.selector}
                                                    onSort={(_header: string, direction: SortDirection) =>
                                                        onSort(column.selector, direction)
                                                    }
                                                    sortDirection={
                                                        defaultSearchParams.sortField !== column.selector ?  SortDirection.Ascending :
                                                        defaultSearchParams.sortType === ServerSortType.Asc
                                                            ? SortDirection.Ascending
                                                            : SortDirection.Descending
                                                    }
                                                >
                                                    {column.title}
                                                </DataTableColumnHeaderCell>
                                            );
                                        return (
                                            <DataTableColumnHeaderCell key={column.selector}>
                                                {column.title}
                                            </DataTableColumnHeaderCell>
                                        );
                                    })}
                                </React.Fragment>
                            </DataTableHeaderRow>
                            <DataTableBody>
                                {organizations.map((row: Organization) => {
                                    return renderRow(row);
                                })}
                            </DataTableBody>
                        </DataTableContent>
                        <DataTablePagination
                            startRow={defaultSearchParams.startIndex}
                            totalRowCount={totalCount}
                            rowsPerPage={defaultSearchParams.recordsToRetrieve}
                            rowsPerPageOptions={[25, 50, 100]}
                            onChangePage={(startRow: number) => {
                                const params = defaultSearchParams;
                                params.startIndex = startRow === 0 ? 0 : startRow;
                                searchOrganizations(params).then((data) => {
                                    setDefaultSearchParams(params);
                                    setOrganizations(data.organizations);
                                    setTotalCount(data.totalRecords);
                                });
                            }}
                            onChangeRowsPerPage={(rowsPerPage: number) => {
                                const params = defaultSearchParams;
                                params.startIndex = 0;
                                params.recordsToRetrieve = rowsPerPage;
                                searchOrganizations(params).then((data) => {
                                    setDefaultSearchParams(params);
                                    setOrganizations(data.organizations);
                                    setTotalCount(data.totalRecords);
                                });
                            }}
                        />
                    </DataTable>
                    {errorOpen && 
                    <Snackbar open={errorOpen}
                        message={
                            <Typography tag="p" use={"body1"}>
                                {error ?? ''}
                            </Typography>
                        }
                        timeout={-1}
                        dismissesOnAction
                        onClose={() => { setErrorOpen(false); }}
                        action={[
                            <SnackbarAction label="Close" />
                        ]}>
                    </Snackbar>}
                </div>
            </Dialog>
        </Fragment>
    );
};

export default AddParentOrganization;